import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {SystemRole, User} from "../../../../_models/user";
import {NotificationService} from "../../../../_services/notification.service";
import {Notification} from "../../../../_models/notification";
import {Router} from "@angular/router";
import {GlobalStore} from "../../../../global.store";
import {Subject, takeUntil} from "rxjs";
import {AuthService} from "../../../../_services/auth.service";
import {SidebarService} from '../sidebar/sidebar.service';

@Component({
    selector: 'app-sub-header',
    templateUrl: './sub-header.component.html',
    styleUrls: ['./sub-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SubHeaderComponent implements OnInit, OnDestroy {
  @ViewChild('notificationContainer') notificationContainer: ElementRef
  @Input() title: string[];

  isSelectUnreadNotif: boolean = false;

  userImg!: string;
  isOpenMenu: boolean = false;
  isNotifSettingsOpen = false;
  isOpenNotifications = false;
  user: User;
  filteredNotificationsList: Notification[] = [];
  isDashboard = false;
  isLoadingMoreNotifications = false;
  hasMoreNotifications = true;

  private readonly destroy$ = new Subject<void>()
  constructor(readonly globalStore: GlobalStore,
              public notificationService: NotificationService,
              public router: Router,
              public authService: AuthService,
              readonly sidebarService: SidebarService,
              private cdRef: ChangeDetectorRef) {
    this.globalStore.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
      this.cdRef.markForCheck();
    });
    this.isDashboard = this.router.routerState.snapshot.url.includes('dashboard')
  }

  ngOnInit() {
    this.notificationService.notificationsChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isOpenNotifications) {
          this.filterNotifications();
          return;
        }

        this.cdRef.markForCheck();
      });

    this.getUnreadNotificationsCount()
  }

  openSidebar() {
    this.sidebarService.setMenuState(true)
    document.querySelector('.sidebar-wrapper').setAttribute('isOpenMenu', 'true')
  }

  openProfileDropDown() {
    this.isOpenMenu = !this.isOpenMenu;
  }

  toggleNotifications() {
    this.isOpenNotifications = !this.isOpenNotifications;
    this.filterNotifications();
  }

  closeNotifications() {
    this.isOpenNotifications = false;
    this.markAsReadNotif();
  }

  markAsReadNotif(){
    this.notificationService.notificationsList =  this.notificationService.notificationsList.map(notification => {
      if(notification.wasRead === true) {
        notification.delivered = true
        return notification
      }
      return notification
    });

    this.getUnreadNotificationsCount()
    this.filterNotifications()
  }

  notifVisible(notif: Notification){
    if (!notif.delivered) {
      this.notificationService.notificationDelivered(notif.id, this.user.id)
      const targetNotification = this.notificationService.notificationsList
        .find(notification => notification.id === notif.id);

      if (targetNotification) {
        targetNotification.wasRead = true;
      }
    }
  }

  loadMoreNotifications() {
    if (
      this.isLoadingMoreNotifications ||
      !this.hasMoreNotifications ||
      this.notificationService.notificationsList.length === 0
    ) {
      return;
    }

    const lastNotification =
      this.notificationService.notificationsList[
        this.notificationService.notificationsList.length - 1
      ];

    if (!lastNotification?.id) {
      this.hasMoreNotifications = false;
      return;
    }

    this.isLoadingMoreNotifications = true;

    this.notificationService.getNotifications(lastNotification.id).subscribe({
      next: (data) => {
        if (!data.length) {
          this.hasMoreNotifications = false;
          return;
        }

        const existingIds = new Set(
          this.notificationService.notificationsList.map(notification => notification.id)
        );

        const nextNotifications = data.filter(
          notification => !existingIds.has(notification.id)
        );

        if (!nextNotifications.length) {
          this.hasMoreNotifications = false;
          return;
        }

        this.notificationService.notificationsList = [
          ...this.notificationService.notificationsList,
          ...nextNotifications
        ];
        this.filterNotifications();
      },
      error: () => {
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.isLoadingMoreNotifications = false;
        this.cdRef.markForCheck();
      }
    });
  }

  isLoadMoreTrigger(index: number): boolean {
    const triggerIndex = Math.max(this.filteredNotificationsList.length - 2, 0);
    return this.hasMoreNotifications && index === triggerIndex;
  }

  openNotification(notification: Notification) {
    this.router.navigateByUrl(notification.link);
    this.closeNotifications();
  }


  getUnreadNotificationsCount() {
    this.notificationService.getCountUnreadNotifications().subscribe((data) => {
      this.notificationService.unreadNotifCount = data
      this.cdRef.markForCheck();
    })
  }

  filterNotifications() {
    this.filteredNotificationsList = this.isSelectUnreadNotif
      ? this.notificationService.notificationsList.filter(notif => notif.delivered === false)
      : [...this.notificationService.notificationsList];

    this.cdRef.markForCheck();
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  protected readonly SystemRole = SystemRole;
}
